import { useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import type { Rive } from '@rive-app/react-canvas';
import type { IRiveFile, IRiveText } from '../types';

export const usePrefix = () => {
  const { t } = useTranslation();
  let pathPrefix = t('langCodes_pathPrefix');
  if (pathPrefix !== '') {
    pathPrefix = `/${pathPrefix}`;
  }

  return pathPrefix;
};

export const useRiveUrl = (riveFile: IRiveFile) => {

  const pathPrefix = usePrefix();
  /*
  * if we're on development, we need to add the domain to the URL. This will result
  * in a CORS error in your browser. You should disable CORS in your browser to
  * see the Rive animation.
  **/
  let riveUrl = riveFile.mediaItemUrl;

  /** 
   * if on production, remove the domain from the URL. This will result in a relative
   * path to the Rive animation. If we were to keep the domain name, we would get a
   * CORS error in the browser.
   **/ 
  if (riveUrl.startsWith('http')) {
    const uploadsIndex = riveUrl.indexOf('/uploads');

    if (uploadsIndex !== -1) {
      riveUrl = `${pathPrefix}/static/rive${riveUrl.substring(uploadsIndex)}`;
    } else {
      riveUrl = (new URL(riveFile.mediaItemUrl)).pathname;
    }
  }

  return riveUrl;
};

/**
 * Custom hook to handle setting text runs in Rive animations
 * @param rive - The Rive instance
 * @param riveText - Array of text run configurations
 */
export const useRiveTextRuns = (rive: Rive | null, riveText?: IRiveText[]) => {
  useEffect(() => {
    // This effect runs when the Rive instance is ready
    if (rive && riveText && riveText.length > 0) {
      riveText.forEach(({ key, value, path }) => {
        if (path) {
          rive.setTextRunValueAtPath(key, value, path);
        } else {
          rive.setTextRunValue(key, value);
        }
      });
    }
  }, [rive, riveText]);
};
